import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { getStoreScheduleSummaryApi, StoreScheduleSummary, WEEKDAY_SHORT } from "../api/scheduleApi";
import { STORE_DETAIL_PAGE } from "../../store/routes";
import BulkStoreAssignmentModal from "./BulkStoreAssignmentModal";

type Store = StoreScheduleSummary["stores"][number];

/**
 * Aba "Lojas e turnos" em Cadastros: a situação de cada loja (horário, posição e
 * lotados) e a lotação em lote. O horário, os turnos e a posição se editam na
 * ficha da loja (`/lojas/:id`), a mesma que o ADMIN abre por Operações → Lojas
 * (plan-notificacoes-whatsapp, 22/10/2026).
 */
export default function StoreSchedulesSettings({ token }: { token: string }) {
  const [summary, setSummary] = useState<StoreScheduleSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState<Store | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSummary(await getStoreScheduleSummaryApi(token));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar as lojas.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const openDays = (days: number[]) => (days.length === 7 ? "Todos os dias" : days.map((d) => WEEKDAY_SHORT[d]).join(", "));

  return (
    <>
      <p className="dpFormHint" style={{ marginBottom: 12 }}>
        Abra a ficha da loja para o horário, os turnos, a posição, a equipe e o quiosque. Lotar e retirar valem na hora; a
        loja de cada um também se troca na ficha do colaborador, aba Escala.
      </p>

      {summary && summary.withoutStore > 0 ? (
        <p className="dpNotice dpNotice--warning">
          {summary.withoutStore} colaborador(es) com contrato ativo ainda sem loja. Use "Lotar ou retirar" na loja de
          cada um.
        </p>
      ) : null}

      {loading && !summary ? (
        <div className="dpEmpty">Carregando...</div>
      ) : (
        <div className="dpTableWrapper">
          <table className="dpTable">
            <thead>
              <tr>
                <th>Loja</th>
                <th>UF</th>
                <th>Abre</th>
                <th>Lotados</th>
                <th>Situação</th>
                <th>Posição</th>
                <th style={{ width: 240 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {!summary || summary.stores.length === 0 ? (
                <tr>
                  <td colSpan={7} className="dpEmpty">
                    Nenhuma loja cadastrada.
                  </td>
                </tr>
              ) : (
                summary.stores.map((store) => (
                  <tr key={store.storeId}>
                    <td>{store.storeName}</td>
                    <td>{store.estado ?? "—"}</td>
                    <td>{openDays(store.openWeekdays)}</td>
                    <td>{store.staffCount}</td>
                    <td>
                      <span className={`dpBadge ${store.configured ? "dpBadge--green" : "dpBadge--gray"}`}>
                        {store.configured ? "Configurada" : "Horário padrão"}
                      </span>
                    </td>
                    <td>
                      <span className={`dpBadge ${store.hasLocation ? "dpBadge--green" : "dpBadge--orange"}`}>{store.hasLocation ? "Cadastrada" : "Sem posição"}</span>
                    </td>
                    <td>
                      <div className="dpTable__actions" style={{ flexWrap: "wrap" }}>
                        <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => navigate(STORE_DETAIL_PAGE(store.storeId))}>
                          Abrir ficha
                        </button>
                        <button className="dpBtn dpBtn--primary dpBtn--small" onClick={() => setAssigning(store)}>
                          Lotar ou retirar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {assigning ? (
        <BulkStoreAssignmentModal
          token={token}
          storeId={assigning.storeId}
          storeName={assigning.storeName}
          onClose={() => setAssigning(null)}
          onDone={() => {
            setAssigning(null);
            load();
          }}
        />
      ) : null}
    </>
  );
}
